import React, {ReactElement} from 'react'
import {
    AdminReserveLocationState,
    AgentReservationInfo,
    ManagerReservationInfo,
    ReservationInfo,
} from '../AdminReserveInterfaces'
import {Table, TableBody, TableCell, TableRow} from '@mui/material'
import {StyledTableCaption} from '../../../../styledComponents/adminPage/Table'
import lsHelper from '../AdminReserveLocationStateHelper'
import {getDisplayRoomNumber, getDisplaySelectedUserName, getDisplayUserNameForManagement} from '../AdminReserveHelper'
import {LeftMarginLink} from '../AdminReserveStyledComponent'
import {BoldSpan} from '../../../../styledComponents/StyledSpan'
import {ReservationClass} from '../../ReservationInterfaces'
import {FixedWidthTableRowName} from './ARDetailsView'
import {NavigateFunction} from 'react-router-dom'

interface CommonProps {
    reservationInfo: ReservationInfo
}

interface PropsForConfirm extends CommonProps {
    type: 'confirm'
    condominiumCd: string
    navigate: NavigateFunction
    locationState: AdminReserveLocationState
}

interface PropsForComplete extends CommonProps {
    type: 'complete'
}

type Props = PropsForConfirm | PropsForComplete

const ARUserView: React.FC<Props> = props => {
    const renderViewForAgent = (reservationInfo: AgentReservationInfo): ReactElement => {
        const {userInfo, paymentType, selectedRoomsOrDisabled} = reservationInfo
        if (userInfo === null) {
            throw Error('利用者の情報がありません。')
        }
        if (paymentType === null) {
            throw Error('支払方法の情報がありません。')
        }

        return (<Table>
            <StyledTableCaption>
                利用者・支払方法
                {
                    props.type === 'confirm' &&
                    <LeftMarginLink onClick={() => {
                        props.navigate(
                            `/admin/condominiums/${props.condominiumCd}/agent-reserve/select-user`,
                            {
                                replace: true,
                                state: lsHelper.toObject(props.locationState),
                            },
                        )
                    }}>修正する</LeftMarginLink>
                }
            </StyledTableCaption>
            <TableBody>
                <TableRow>
                    <FixedWidthTableRowName>部屋番号</FixedWidthTableRowName>
                    <TableCell>
                        <BoldSpan>
                            {getDisplayRoomNumber(userInfo)}
                        </BoldSpan>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <FixedWidthTableRowName>氏名</FixedWidthTableRowName>
                    <TableCell>
                        <BoldSpan>
                            {getDisplaySelectedUserName(userInfo)}
                        </BoldSpan>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <FixedWidthTableRowName>管理者確認用氏名</FixedWidthTableRowName>
                    <TableCell>
                        <BoldSpan>
                            {getDisplayUserNameForManagement(userInfo)}
                        </BoldSpan>
                    </TableCell>
                </TableRow>
                <TableRow>
                    <FixedWidthTableRowName>お支払方法</FixedWidthTableRowName>
                    <TableCell>
                        <BoldSpan>
                            {paymentType.label}
                        </BoldSpan>
                    </TableCell>
                </TableRow>
                {
                    selectedRoomsOrDisabled !== null &&
                    <TableRow>
                        <FixedWidthTableRowName>同時入室番号</FixedWidthTableRowName>
                        <TableCell>
                            <BoldSpan>
                                {
                                    selectedRoomsOrDisabled
                                        .sort((a, b) => {
                                            const aName = `${a.ridgeCd}${a.roomNumber}`
                                            const bName = `${b.ridgeCd}${b.roomNumber}`
                                            if (aName === bName) {
                                                return 0
                                            }
                                            return (aName > bName) ? 1 : -1
                                        })
                                        .map(info => `${info.ridgeName} ${info.roomNumber}`)
                                        .join(', ')
                                }
                            </BoldSpan>
                        </TableCell>
                    </TableRow>
                }
            </TableBody>
        </Table>)
    }

    const renderViewForManager = (reservationInfo: ManagerReservationInfo): ReactElement => {
        const {userInfo, selectedRoomsOrDisabled} = reservationInfo
        if (userInfo === null) {
            throw Error('利用者の情報がありません。')
        }

        return (<Table>
            <StyledTableCaption>
                利用者
                {
                    props.type === 'confirm' &&
                    <LeftMarginLink onClick={() => {
                        props.navigate(
                            `/admin/condominiums/${props.condominiumCd}/manager-reserve/select-user`,
                            {
                                replace: true,
                                state: lsHelper.toObject(props.locationState),
                            },
                        )
                    }}>修正する</LeftMarginLink>
                }
            </StyledTableCaption>
            <TableBody>
                <TableRow>
                    <FixedWidthTableRowName>氏名</FixedWidthTableRowName>
                    <TableCell>
                        <BoldSpan>
                            {`${userInfo.familyName} ${userInfo.firstName}`}
                        </BoldSpan>
                    </TableCell>
                </TableRow>
                {
                    selectedRoomsOrDisabled !== null &&
                    <TableRow>
                        <FixedWidthTableRowName>同時入室番号</FixedWidthTableRowName>
                        <TableCell>
                            <BoldSpan>
                                {
                                    selectedRoomsOrDisabled
                                        .sort((a, b) => {
                                            const aName = `${a.ridgeCd}${a.roomNumber}`
                                            const bName = `${b.ridgeCd}${b.roomNumber}`
                                            if (aName === bName) {
                                                return 0
                                            }
                                            return (aName > bName) ? 1 : -1
                                        })
                                        .map(info => `${info.ridgeName} ${info.roomNumber}`)
                                        .join(', ')
                                }
                            </BoldSpan>
                        </TableCell>
                    </TableRow>
                }
            </TableBody>
        </Table>)
    }

    if (props.reservationInfo.classification === ReservationClass.AGENT) {
        return renderViewForAgent(props.reservationInfo)
    } else {
        return renderViewForManager(props.reservationInfo)
    }
}

export default ARUserView
